// Environment variable utility that works in both runtime and test environments
// This avoids import.meta issues in Jest tests

interface ImportMetaEnv {
  VITE_API_KEY?: string;
  VITE_API_URL?: string;
  VITE_STORAGE_URL?: string;
  VITE_LOG_ENDPOINT?: string;
  VITE_LOG_API_KEY?: string;
  VITE_ENABLE_USER_TRACKING?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  DEV?: boolean;
}

// Safe environment variable access that works in both runtime and test environments
const getEnv = (key: keyof ImportMetaEnv): string | boolean | undefined => {
  // In test environment, use global mocks or defaults
  if (typeof window !== 'undefined' && (window as any).__VITE_ENV_MOCK__) {
    return (window as any).__VITE_ENV_MOCK__[key];
  }
  
  // In runtime environment, check for __IMPORT_META_ENV__ injected by Jest setup
  if (typeof window !== 'undefined' && (window as any).__IMPORT_META_ENV__) {
    return (window as any).__IMPORT_META_ENV__[key];
  }
  
  // Use import.meta.env directly if available
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env[key];
    }
  } catch (e) {
    // import.meta not available in this environment
  }
  
  // Fallback to process.env if available (Node.js environments)
  if (typeof process !== 'undefined' && process.env) {
    const value = process.env[key];
    if (value !== undefined && value !== '') {
      return value;
    }
  }
  
  // Development defaults for when environment variables are not set
  const defaults: ImportMetaEnv = {
    // VITE_API_URL: 'https://lgwoe786c7.execute-api.eu-central-1.amazonaws.com/staging/api/v1',
    VITE_API_URL: 'http://localhost:4000/staging/api/v1',
    VITE_STORAGE_URL: 'http://localhost:3000/storage',
    VITE_API_KEY: 'development-key',
    VITE_LOG_ENDPOINT: '',
    VITE_LOG_API_KEY: '',
    VITE_ENABLE_USER_TRACKING: 'false',
    VITE_SENTRY_DSN: '',
    VITE_SENTRY_ENVIRONMENT: 'development',
    VITE_SENTRY_RELEASE: 'unknown',
    DEV: true
  };
  
  return defaults[key];
};

// Debug: Log the actual import.meta.env to see what's available
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  try {
    if ((window as any).__IMPORT_META_ENV__) {
      console.log('Available env vars:', (window as any).__IMPORT_META_ENV__);
    } else {
      const importMeta = (globalThis as any).import?.meta || (window as any).import?.meta;
      console.log('Available env vars:', importMeta?.env);
    }
  } catch (e) {
    console.log('Could not access environment variables');
  }
}

// Typed environment variable getters
export const env = {
  get apiKey() { return getEnv('VITE_API_KEY') as string; },
  get apiUrl() { return getEnv('VITE_API_URL') as string; },
  get storageUrl() { return getEnv('VITE_STORAGE_URL') as string; },
  get logEndpoint() { return getEnv('VITE_LOG_ENDPOINT') as string; },
  get logApiKey() { return getEnv('VITE_LOG_API_KEY') as string; },
  get enableUserTracking() { return getEnv('VITE_ENABLE_USER_TRACKING') === 'true'; },
  get sentryDsn() { return getEnv('VITE_SENTRY_DSN') as string; },
  get sentryEnvironment() { return getEnv('VITE_SENTRY_ENVIRONMENT') as string; },
  get sentryRelease() { return getEnv('VITE_SENTRY_RELEASE') as string; },
  get isDev() { return getEnv('DEV') as boolean; }
};

export default env; 